import { useEffect, useState } from 'react';
import axios from 'axios';
import { Server, Plus, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

const MODALITIES = [
  { value: 'reasoning', label: 'Reasoning (LLM)' },
  { value: 'image', label: 'Image Generation' },
  { value: 'video', label: 'Video Generation' },
  { value: 'audio', label: 'Audio/Music' },
  { value: 'avatar', label: 'Voice/Avatar' },
];

export default function AIProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    provider_name: '',
    supported_modalities: [],
    base_api_url: '',
    priority: 50,
    description: '',
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingProvider) {
        await axios.put(
          `${API}/providers/${editingProvider.provider_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Provider updated successfully');
      } else {
        await axios.post(`${API}/providers`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Provider created successfully');
      }
      
      setShowDialog(false);
      setEditingProvider(null);
      resetForm();
      fetchProviders();
    } catch (error) {
      console.error('Error saving provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      provider_name: provider.provider_name,
      supported_modalities: provider.supported_modalities,
      base_api_url: provider.base_api_url || '',
      priority: provider.priority,
      description: provider.description || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Are you sure? This will fail if the provider has credentials or models.')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Provider deleted successfully');
      fetchProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete provider');
    }
  };

  const toggleStatus = async (provider) => {
    try {
      const token = localStorage.getItem('admin_token');
      const newStatus = provider.status === 'active' ? 'inactive' : 'active';
      
      await axios.put(
        `${API}/providers/${provider.provider_id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Provider ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      fetchProviders();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      provider_name: '',
      supported_modalities: [],
      base_api_url: '',
      priority: 50,
      description: '',
    });
  };

  const handleModalityToggle = (modality) => {
    setFormData(prev => ({
      ...prev,
      supported_modalities: prev.supported_modalities.includes(modality)
        ? prev.supported_modalities.filter(m => m !== modality)
        : [...prev.supported_modalities, modality]
    }));
  };

  const getStatusBadge = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800';
  };

  const getModalityBadge = (modality) => {
    const colors = {
      reasoning: 'bg-blue-100 text-blue-800',
      image: 'bg-purple-100 text-purple-800',
      video: 'bg-pink-100 text-pink-800',
      audio: 'bg-green-100 text-green-800',
      avatar: 'bg-yellow-100 text-yellow-800',
    };
    return colors[modality] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Loading AI providers...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">AI Providers</h1>
          <p className="text-slate-600">Manage AI service providers and their configurations</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingProvider(null);
            setShowDialog(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Provider
        </Button>
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {providers.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow-md">
            <Server className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 mb-4">No AI providers configured yet</p>
            <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Provider
            </Button>
          </div>
        ) : (
          providers.map((provider) => (
            <div
              key={provider.provider_id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <Server className="w-8 h-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="font-semibold text-lg text-slate-800">{provider.provider_name}</h3>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusBadge(provider.status)}`}>
                      {provider.status}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => toggleStatus(provider)}
                  className={`p-2 rounded-lg ${provider.status === 'active' ? 'text-green-600 hover:bg-green-50' : 'text-gray-600 hover:bg-gray-50'}`}
                  title={provider.status === 'active' ? 'Disable' : 'Enable'}
                >
                  {provider.status === 'active' ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-slate-600 mb-2">{provider.description || 'No description'}</p>
                {provider.base_api_url && (
                  <p className="text-xs text-slate-500 truncate">{provider.base_api_url}</p>
                )}
              </div>

              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-2">Capabilities:</div>
                <div className="flex flex-wrap gap-2">
                  {provider.supported_modalities.map((modality) => (
                    <span
                      key={modality}
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getModalityBadge(modality)}`}
                    >
                      {modality}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                <span>Priority: {provider.priority}</span>
                <span>{new Date(provider.created_at).toLocaleDateString()}</span>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(provider)}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(provider.provider_id)}
                  className="flex-1 flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingProvider ? 'Edit Provider' : 'Add New Provider'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="provider_name">Provider Name *</Label>
                <Input
                  id="provider_name"
                  placeholder="e.g., Google Gemini, OpenAI"
                  value={formData.provider_name}
                  onChange={(e) => setFormData({...formData, provider_name: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label>Supported Modalities *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {MODALITIES.map((modality) => (
                    <label
                      key={modality.value}
                      className="flex items-center space-x-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.supported_modalities.includes(modality.value)}
                        onChange={() => handleModalityToggle(modality.value)}
                        className="rounded"
                      />
                      <span className="text-sm">{modality.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="base_api_url">Base API URL</Label>
                <Input
                  id="base_api_url"
                  placeholder="https://api.example.com"
                  value={formData.base_api_url}
                  onChange={(e) => setFormData({...formData, base_api_url: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="priority">Priority (Higher = Preferred)</Label>
                <Input
                  id="priority"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of this provider"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  setEditingProvider(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingProvider ? 'Update Provider' : 'Create Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
